import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { programFromContent } from "./mockData";
import { SaveDayError } from "./api";
import { programToApiBlock } from "./apiMapping";
import {
  absSpan,
  findFreeSlot,
  MAX_END_MINUTES,
  overwriteRange,
  programsFor,
  shiftDateKey,
  slotMinutesFor,
  stripDateKeys,
  toHHmm,
  toMinutes,
  validatePlacement,
} from "./schedulerUtils";
import { etDateKey } from "@/v2/lib/timezone";
import { useRemoteSchedule } from "./useRemoteSchedule";
import type { ContentItem, PlacementResult, Program, SchedulerView } from "./types";

const NO_GAMES: ContentItem[] = [];
const OK: PlacementResult = { ok: true };
const fail = (error: string): PlacementResult => ({ ok: false, error });

/** "Changes save automatically": structural edits flush this soon after the
 * gesture (long enough to coalesce one drop's state updates, short enough
 * to feel immediate). */
export const AUTOSAVE_DEBOUNCE_MS = 300;

const dayKey = (channelId: string, date: string) => `${channelId}|${date}`;

/** The fields a PATCH may carry, diffed via the wire representation. */
function changedFields(before: Program, after: Program) {
  const a = programToApiBlock(before);
  const b = programToApiBlock(after);
  const fields: Record<string, unknown> = {};
  for (const key of ["contentType", "contentId", "startMin", "endMin", "trimInSec", "trimOutSec", "durationSec", "title", "description"] as const) {
    if (a[key] !== b[key]) fields[key] = b[key];
  }
  if (JSON.stringify(a.metadata) !== JSON.stringify(b.metadata)) fields.metadata = b.metadata;
  if (before.channelId !== after.channelId) fields.channelId = Number(after.channelId);
  if (before.date !== after.date) fields.date = after.date;
  return fields;
}

/**
 * All scheduler state in one place: the selected day, Day/Week view, the
 * program list and the selected program. Every mutation validates first and
 * returns a result so the UI can toast the reason instead of silently
 * dropping the change. Switching view never touches the programs.
 *
 * Data flows through useRemoteSchedule: channels and the stored schedule
 * come from the API, every mutation marks its channel-day dirty, and a
 * debounced flush PUTs each dirty day's full list (the server replaces the
 * day and returns rows carrying its own ids, which win locally).
 */
export function useChannelScheduler(realGames: ContentItem[] = NO_GAMES) {
  // Today on the fixed Eastern broadcast day, which is what the grid, the
  // stored rows and the vendor manifests all mean by a date.
  const todayKey = useMemo(() => etDateKey(new Date()), []);
  // The backend only stores games for now, so the library is games only.
  const content = realGames;
  const [programs, setPrograms] = useState<Program[]>([]);
  const [date, setDate] = useState(todayKey);
  const [view, setView] = useState<SchedulerView>("day");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const nextId = useRef(1);

  // The day view's continuous scrollable strip: one day back (so
  // yesterday's overnight spill is visible in full) through the selected
  // date and the days after it, rendered as one timeline so blocks can
  // cross midnight. The view opens scrolled to the selected date.
  const stripDays = useMemo(() => stripDateKeys(date), [date]);

  const remote = useRemoteSchedule(view, date);

  // True only until the first channels + schedule fetch both land, so a
  // day/view switch (which re-fetches under a new query key) doesn't
  // re-trigger the skeleton and flash away the grid that's already on screen.
  const hasLoadedOnceRef = useRef(false);
  const isLoading =
    !hasLoadedOnceRef.current && (remote.channels.length === 0 || remote.programs === undefined);
  useEffect(() => {
    if (remote.channels.length > 0 && remote.programs !== undefined) {
      hasLoadedOnceRef.current = true;
    }
  }, [remote.channels, remote.programs]);

  // Refs the async flush reads so it always sees the latest state without
  // re-arming on every render.
  const programsRef = useRef(programs);
  useEffect(() => {
    programsRef.current = programs;
  }, [programs]);
  const dirtyRef = useRef(new Set<string>());
  // Last server-confirmed rows per channel-day: what the diff compares
  // against to turn local edits into per-program CRUD calls.
  const baselineRef = useRef(new Map<string, Program[]>());
  const flushTimer = useRef<number | null>(null);

  /**
   * Diff the dirty channel-days against their baselines and emit one call
   * per changed program (team decision: per-gesture REST, not day
   * replaces): DELETE for ids gone from the day set, PATCH with only the
   * changed fields (channelId/date included on a move), POST for programs
   * the server has never seen. Deletes go first so a block moved into a
   * freed slot validates.
   */
  const flush = useCallback(async (): Promise<boolean> => {
    let allSaved = true;
    const keys = [...dirtyRef.current];
    dirtyRef.current.clear();
    if (keys.length === 0) return true;

    const baseline = new Map<string, Program>();
    for (const key of keys) {
      for (const p of baselineRef.current.get(key) ?? []) baseline.set(p.id, p);
    }
    const inScope = (p: Program) => keys.includes(dayKey(p.channelId, p.date));
    const current = new Map<string, Program>();
    // A block still waiting on its exact runtime is not saved yet: it will
    // mark its day dirty again once the duration lands.
    for (const p of programsRef.current) {
      if (inScope(p) && !p.pendingDuration) current.set(p.id, p);
    }

    const failedKeys = new Set<string>();
    const report = (e: unknown, ...affected: string[]) => {
      allSaved = false;
      affected.forEach((k) => failedKeys.add(k));
      const detail =
        e instanceof SaveDayError && e.blockErrors[0] ? `: ${e.blockErrors[0].error}` : "";
      toast.error(`${e instanceof Error ? e.message : "Saving the schedule failed"}${detail}`);
    };

    for (const [id, was] of baseline) {
      if (current.has(id)) continue;
      try {
        await remote.removeProgram(was.channelId, was.date, id);
        baseline.delete(id);
      } catch (e) {
        report(e, dayKey(was.channelId, was.date));
      }
    }

    for (const [id, now] of current) {
      const was = baseline.get(id);
      if (!was) continue;
      const fields = changedFields(was, now);
      if (Object.keys(fields).length === 0) continue;
      try {
        const saved = await remote.patchProgram(was.channelId, was.date, id, fields);
        baseline.set(id, saved);
      } catch (e) {
        report(e, dayKey(was.channelId, was.date), dayKey(now.channelId, now.date));
      }
    }

    for (const [id, now] of [...current]) {
      if (baseline.has(id)) continue;
      try {
        const saved = await remote.addProgram(now);
        // The server id replaces the local placement id everywhere.
        baseline.set(saved.id, saved);
        setPrograms((prev) => prev.map((p) => (p.id === id ? saved : p)));
        setSelectedId((sel) => (sel === id ? saved.id : sel));
        current.delete(id);
        current.set(saved.id, saved);
      } catch (e) {
        report(e, dayKey(now.channelId, now.date));
      }
    }

    for (const key of keys) {
      if (failedKeys.has(key)) {
        // A rejected save usually means this tab's view of the day is
        // stale (another tab or admin changed it). Drop the local claim
        // and refetch, so the day snaps back to server truth; the toast
        // above told the admin what was refused.
        dirtyRef.current.delete(key);
      } else {
        baselineRef.current.set(
          key,
          [...baseline.values()].filter((p) => dayKey(p.channelId, p.date) === key),
        );
      }
    }
    if (failedKeys.size > 0) remote.refetchDays();
    return allSaved;
  }, [remote.removeProgram, remote.patchProgram, remote.addProgram, remote.refetchDays]);

  const markDirty = useCallback(
    (channelId: string, day: string, { schedule = true } = {}) => {
      dirtyRef.current.add(dayKey(channelId, day));
      // schedule: false stages the day without arming the autosave timer:
      // Edit Program adjustments (trim, description) apply locally and wait
      // for the Save button, while structural timeline changes still
      // autosave. A staged day does ride along if something else flushes,
      // since a day-replace PUT always carries the day's current state.
      if (!schedule) return;
      if (flushTimer.current !== null) window.clearTimeout(flushTimer.current);
      flushTimer.current = window.setTimeout(() => {
        flushTimer.current = null;
        void flush();
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  useEffect(
    () => () => {
      if (flushTimer.current !== null) window.clearTimeout(flushTimer.current);
    },
    [],
  );

  /** The Save button's path: skip the debounce and flush everything dirty
   * right now. Resolves true when every dirty day saved. */
  const saveNow = useCallback(async (): Promise<boolean> => {
    if (flushTimer.current !== null) {
      window.clearTimeout(flushTimer.current);
      flushTimer.current = null;
    }
    return flush();
  }, [flush]);

  // Fetched days replace local state, except days with unsaved edits, and
  // become the diff baselines for every clean channel-day in the range.
  useEffect(() => {
    if (!remote.programs) return;
    const fetched = remote.programs;
    const fetchedDays = new Set(remote.rangeDays);
    const isDirty = (p: Program) => dirtyRef.current.has(dayKey(p.channelId, p.date));
    setPrograms((prev) => [
      ...prev.filter((p) => !fetchedDays.has(p.date) || isDirty(p)),
      ...fetched.filter((p) => fetchedDays.has(p.date) && !isDirty(p)),
    ]);
    for (const day of remote.rangeDays) {
      for (const channel of remote.channels) {
        const key = dayKey(channel.id, day);
        if (dirtyRef.current.has(key)) continue;
        baselineRef.current.set(
          key,
          fetched.filter((p) => p.channelId === channel.id && p.date === day),
        );
      }
    }
  }, [remote.programs, remote.rangeDays, remote.channels]);

  // Open with the day's first program selected so the Edit panel is populated.
  const autoSelected = useRef(false);
  useEffect(() => {
    if (autoSelected.current || remote.channels.length === 0) return;
    const first = programsFor(programs, remote.channels[0].id, date)[0];
    if (first) {
      autoSelected.current = true;
      setSelectedId((current) => current ?? first.id);
    }
  }, [programs, remote.channels, date]);

  const selected = useMemo(
    () => programs.find((p) => p.id === selectedId) ?? null,
    [programs, selectedId],
  );

  const shiftDate = useCallback((days: number) => setDate((d) => shiftDateKey(d, days)), []);

  const movePlacement = useCallback(
    (id: string, channelId: string, toDate: string, startMin: number): PlacementResult => {
      const program = programs.find((p) => p.id === id);
      if (!program) return fail("Program not found.");
      if (program.pendingDuration) return fail("Waiting for this game's exact runtime.");
      const length = toMinutes(program.endTime) - toMinutes(program.startTime);
      const endMin = startMin + length;
      const error = validatePlacement(programs, {
        channelId,
        date: toDate,
        startMin,
        endMin,
        ignoreId: id,
      });
      if (error) return fail(error);
      setPrograms((list) =>
        list.map((p) =>
          p.id === id
            ? { ...p, channelId, date: toDate, startTime: toHHmm(startMin), endTime: toHHmm(endMin) }
            : p,
        ),
      );
      markDirty(program.channelId, program.date);
      markDirty(channelId, toDate);
      return OK;
    },
    [programs, markDirty],
  );

  const setTrim = useCallback(
    (id: string, trimInSec: number, trimOutSec: number): PlacementResult => {
      const program = programs.find((p) => p.id === id);
      if (!program) return fail("Program not found.");
      if (program.pendingDuration) return fail("Waiting for this game's exact runtime.");
      if (trimInSec < 0 || trimOutSec <= trimInSec) return fail("Trim out must be after trim in.");
      if (trimOutSec > program.durationSec) return fail("Trim out cannot exceed the content duration.");
      // The trimmed span is what airs, so it sets the block's length from its
      // start. Growing past a neighbour overwrites it, like placing new
      // content does, and may run across midnight into the next day.
      const startMin = toMinutes(program.startTime);
      const endMin = startMin + Math.max(1, Math.ceil((trimOutSec - trimInSec) / 60));
      if (endMin > MAX_END_MINUTES) return fail("A program can cross at most one midnight.");
      const { programs: rest, affected, touchedDates } = overwriteRange(
        programs.filter((p) => p.id !== id),
        { channelId: program.channelId, date: program.date, startMin, endMin },
        () => `p-split-${nextId.current++}`,
      );
      setPrograms([...rest, { ...program, trimInSec, trimOutSec, endTime: toHHmm(endMin) }]);
      markDirty(program.channelId, program.date, { schedule: false });
      // Days whose blocks were cut by the growth stage too (a cut on the
      // next day must save with its own channel-day).
      for (const day of touchedDates) {
        if (day !== program.date) markDirty(program.channelId, day, { schedule: false });
      }
      return { ok: true, replaced: affected };
    },
    [programs, markDirty],
  );

  /**
   * Drag one edge of a placed block on the timeline. The "end" handle moves
   * only trimOutSec (matches setTrim: the block's start never moves for it).
   * The "start" handle moves the block's own schedule position AND
   * trimInSec together, by the same amount, so the content trimmed off the
   * front is exactly what the moved edge no longer covers, while the other
   * edge stays put, the way dragging a clip's edge in a video timeline does.
   *
   * A handle stops flush at the neighbouring block (and at the content's
   * own limits) instead of overwriting it: dragging an edge into the next
   * clip pins it at that clip's boundary, so blocks butt up back-to-back
   * for a continuous stream. The block preview clamps the same way; this
   * clamp is the authority in case they disagree.
   */
  const resizeTrim = useCallback(
    (id: string, edge: "start" | "end", deltaMin: number): PlacementResult => {
      const program = programs.find((p) => p.id === id);
      if (!program) return fail("Program not found.");
      if (program.pendingDuration) return fail("Waiting for this game's exact runtime.");
      const startMin = toMinutes(program.startTime);
      const endMin = toMinutes(program.endTime);
      // Neighbouring blocks on the same channel, on the axis anchored at
      // this program's day (a spill-over from yesterday lands here as a
      // positive end, a next-day block as a start past 1440).
      const spans = programs
        .filter((p) => p.id !== id && p.channelId === program.channelId)
        .map((p) => absSpan(p, program.date));
      const prevEnds = spans.map((s) => s.end).filter((e) => e <= startMin);
      const nextStarts = spans.map((s) => s.start).filter((s) => s >= endMin);

      if (edge === "end") {
        const maxLenMin = Math.max(1, Math.floor((program.durationSec - program.trimInSec) / 60));
        const newEndMin = Math.max(
          startMin + 1,
          Math.min(
            endMin + deltaMin,
            nextStarts.length ? Math.min(...nextStarts) : MAX_END_MINUTES,
            startMin + maxLenMin,
            MAX_END_MINUTES,
          ),
        );
        if (newEndMin === endMin) return OK;
        const trimOutSec = program.trimInSec + (newEndMin - startMin) * 60;
        setPrograms((list) =>
          list.map((p) => (p.id === id ? { ...p, trimOutSec, endTime: toHHmm(newEndMin) } : p)),
        );
        // Unlike the Edit Program trim fields, dragging a handle is a
        // structural timeline gesture like a move or a drop, so it
        // autosaves instead of waiting on the Save button.
        markDirty(program.channelId, program.date);
        return OK;
      }

      const newStartMin = Math.min(
        endMin - 1,
        Math.max(
          startMin + deltaMin,
          prevEnds.length ? Math.max(...prevEnds) : 0,
          0,
          startMin - Math.floor(program.trimInSec / 60),
        ),
      );
      if (newStartMin === startMin) return OK;
      const trimInSec = program.trimInSec + (newStartMin - startMin) * 60;
      if (trimInSec >= program.trimOutSec) return fail("Trim in must be before trim out.");
      setPrograms((list) =>
        list.map((p) =>
          p.id === id ? { ...p, trimInSec, startTime: toHHmm(newStartMin) } : p,
        ),
      );
      markDirty(program.channelId, program.date);
      return OK;
    },
    [programs, markDirty],
  );

  const remove = useCallback(
    (id: string) => {
      const program = programsRef.current.find((p) => p.id === id);
      setPrograms((list) => list.filter((p) => p.id !== id));
      setSelectedId((current) => (current === id ? null : current));
      if (program) markDirty(program.channelId, program.date);
    },
    [markDirty],
  );

  /**
   * Place a library item. With a start time it goes exactly there, refusing
   * (like moving an existing program does) if that would overlap whatever is
   * already scheduled there. A null start picks the first free slot on that
   * day instead.
   */
  /**
   * The VOD feed has no runtime, so a dropped game lands as an assumed-3h
   * tile in a pending state (grayed, locked, unsaved) while the exact
   * runtime is probed; the tile then resizes to the real length and saves.
   * A probe miss keeps the estimate and saves anyway, with a heads-up.
   */
  const resolveDuration = useCallback(
    async (id: string, item: ContentItem) => {
      const durationSec = await remote.getGameDuration(item.id.replace(/^vod-/, ""));
      const program = programsRef.current.find((p) => p.id === id);
      if (!program) return;
      if (durationSec === null) {
        toast.info("Exact runtime unavailable; keeping the estimated length.");
        setPrograms((list) =>
          list.map((p) => (p.id === id ? { ...p, pendingDuration: false } : p)),
        );
        markDirty(program.channelId, program.date);
        return;
      }
      const startMin = toMinutes(program.startTime);
      // The real runtime may carry the block across midnight, but never
      // into a neighbour (same day or the next): the block clamps to the
      // free gap and simply airs truncated. Neighbours compare on the
      // absolute axis anchored at this program's day.
      const nextStart = Math.min(
        MAX_END_MINUTES,
        ...programsRef.current
          .filter((p) => p.id !== id && p.channelId === program.channelId)
          .map((p) => absSpan(p, program.date).start)
          .filter((start) => start > startMin),
      );
      // Exact minutes, not the drop grid's 15-minute snap: the tile ends
      // at start + real runtime (rounded up to the minute it finishes in).
      const endMin = Math.min(startMin + Math.max(1, Math.ceil(durationSec / 60)), nextStart);
      const airsSec = Math.min(durationSec, (endMin - startMin) * 60);
      setPrograms((list) =>
        list.map((p) =>
          p.id === id
            ? {
                ...p,
                pendingDuration: false,
                durationSec,
                trimInSec: 0,
                trimOutSec: airsSec,
                endTime: toHHmm(endMin),
              }
            : p,
        ),
      );
      markDirty(program.channelId, program.date);
    },
    [remote.getGameDuration, markDirty],
  );

  const addContent = useCallback(
    (item: ContentItem, channelId: string, toDate: string, startMin: number | null): PlacementResult => {
      const length = slotMinutesFor(item.durationSec);
      const id = `p-new-${nextId.current++}`;
      // Only a game has a runtime to probe (by its game id); a whizzard
      // missing its Duration keeps the estimate, like an upload would.
      const pending = Boolean(item.durationEstimated) && item.type === "game";
      const place = (at: number) => ({
        ...programFromContent(item, channelId, toDate, at, id),
        ...(pending ? { pendingDuration: true } : {}),
      });
      const settle = () => {
        setSelectedId(id);
        if (pending) {
          void resolveDuration(id, item);
        } else {
          markDirty(channelId, toDate);
        }
      };

      if (startMin === null) {
        const start = findFreeSlot(programs, channelId, toDate, length);
        if (start === null) return fail("No free space left on this channel for that day.");
        setPrograms((list) => [...list, place(start)]);
        settle();
        return OK;
      }

      const endMin = startMin + length;
      const error = validatePlacement(programs, { channelId, date: toDate, startMin, endMin });
      if (error) return fail(error);
      setPrograms((list) => [...list, place(startMin)]);
      settle();
      return OK;
    },
    [programs, markDirty, resolveDuration],
  );

  /** Admin-authored program title shown on the block and in the guide;
   * staged like the description (waits for the Save button) and never
   * blank: the guide and the backend both require a title. */
  const setTitle = useCallback(
    (id: string, title: string): PlacementResult => {
      const program = programs.find((p) => p.id === id);
      if (!program) return fail("Program not found.");
      const trimmed = title.trim();
      if (!trimmed) return fail("Title cannot be empty.");
      setPrograms((list) => list.map((p) => (p.id === id ? { ...p, title: trimmed } : p)));
      markDirty(program.channelId, program.date, { schedule: false });
      return OK;
    },
    [programs, markDirty],
  );

  /** Admin-authored guide copy; empty text clears it. */
  const setDescription = useCallback(
    (id: string, description: string): PlacementResult => {
      const program = programs.find((p) => p.id === id);
      if (!program) return fail("Program not found.");
      setPrograms((list) =>
        list.map((p) => (p.id === id ? { ...p, description: description.trim() || null } : p)),
      );
      markDirty(program.channelId, program.date, { schedule: false });
      return OK;
    },
    [programs, markDirty],
  );

  return {
    todayKey,
    channels: remote.channels,
    content,
    programs,
    isLoading,
    date,
    stripDays,
    view,
    selected,
    selectedId,
    setDate,
    setView,
    shiftDate,
    select: setSelectedId,
    movePlacement,
    setTrim,
    resizeTrim,
    remove,
    addContent,
    setTitle,
    setDescription,
    saveNow,
  };
}

export type ChannelScheduler = ReturnType<typeof useChannelScheduler>;
